<template>
  <div class="shear-diagram-wrapper">
    <div ref="diagram"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'PunchingShearDiagram',
  props: {
    column_width: Number,
    column_depth: Number,
    d: Number,
    shear_perimeter_polygon: Array,
    segment_stresses: Array,
    cx: Number,
    cy: Number,
  },
  data() {
    return {
      selected_component: 'v_total',
      components: [
        { value: 'v_Fz',    label: 'vFz' },
        { value: 'v_Mx',    label: 'vMx' },
        { value: 'v_My',    label: 'vMy' },
        { value: 'v_total', label: 'vf'  },
      ],
    };
  },
  mounted() {
    this.draw();
  },
  watch: {
    column_width() { this.draw(); },
    column_depth() { this.draw(); },
    d() { this.draw(); },
    shear_perimeter_polygon: { deep: true, handler() { this.draw(); } },
    segment_stresses: { deep: true, handler() { this.draw(); } },
    cx() { this.draw(); },
    cy() { this.draw(); },
    selected_component() { this.draw(); },
  },
  methods: {
    draw() {
      const colors = {
        column:       '#273c75',
        perimeter:    '#568e49',
        excluded:     '#d4b4c4',
        stressFill:   '#ccec84',
        stressStroke: '#568e49',
        neutral:      '#248894',
        axis:         '#273c75',
        text:         '#273c75',
      };

      const el = this.$refs.diagram;
      d3.select(el).selectAll('*').remove();

      const svgW = 400;
      const svgH = 400;

      const svg = d3.select(el)
        .append('svg')
        .attr('width', svgW)
        .attr('height', svgH);

      const halfC1 = this.column_width / 2;
      const halfC2 = this.column_depth / 2;
      const halfD  = this.d / 2;
      const extentX = halfC1 + halfD;
      const extentY = halfC2 + halfD;

      // --- Sizing: perimeter + gap + band fills available space ---
      const svgMargin       = 40;
      const initialGapPx    = 15;
      const maxBandPx       = 70;
      const availableRadius = (svgW / 2) - svgMargin;
      const perimeterHalfExtent = Math.max(extentX, extentY);
      const scale = (availableRadius - initialGapPx - maxBandPx) / perimeterHalfExtent;

      const ox = svgW / 2;
      const oy = svgH / 2;

      const tx = x => ox + x * scale;
      const ty = y => oy - y * scale;

      // --- Dropdown via foreignObject ---
      const fo = svg.append('foreignObject')
        .attr('x', 110)
        .attr('y', 380)
        .attr('width', 220)
        .attr('height', 24);

      const div = fo.append('xhtml:div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('gap', '6px');

      div.append('xhtml:span')
        .style('font-family', "'Source Serif Pro', serif")
        .style('font-size', '12px')
        .style('color', '#273c75')
        .style('white-space', 'nowrap')
        .text('Stress Component:');

      const select = div.append('xhtml:select')
        .style('font-family', "'Source Serif Pro', serif")
        .style('font-size', '12px')
        .style('background', 'transparent')
        .style('border', 'none')
        .style('border-bottom', '1px solid #000')
        .style('outline', 'none')
        .style('width', '60px')
        .style('height', '20px')
        .style('padding', '0 4px')
        .style('color', '#273c75')
        .style('cursor', 'pointer');

      this.components.forEach(item => {
        select.append('xhtml:option')
          .attr('value', item.value)
          .property('selected', item.value === this.selected_component)
          .text(item.label);
      });

      select.on('change', (event) => {
        this.selected_component = event.target.value;
      });

      const g = svg.append('g');

      // --- Stress scale ---
      const allStressVals = this.segment_stresses.flatMap(seg => [
        Math.abs(seg.stress_i[this.selected_component]),
        Math.abs(seg.stress_j[this.selected_component]),
      ]);
      const maxStress = Math.max(...allStressVals) || 1;
      const stressToPx = v => (v / maxStress) * maxBandPx;

      // --- Defs ---
      const defs = svg.append('defs');
      const pattern = defs.append('pattern')
        .attr('id', 'hatch-stress')
        .attr('patternUnits', 'userSpaceOnUse')
        .attr('width', 6)
        .attr('height', 6)
        .attr('patternTransform', 'rotate(45)');
      pattern.append('line')
        .attr('x1', 0).attr('y1', 0)
        .attr('x2', 0).attr('y2', 6)
        .attr('stroke', colors.stressStroke)
        .attr('stroke-width', 1);

      // --- Stress diagrams ---
      this.segment_stresses.forEach(seg => {
        if (seg.excluded) return;

        const dxS = tx(seg.point_j.x) - tx(seg.point_i.x);
        const dyS = ty(seg.point_j.y) - ty(seg.point_i.y);
        const lenS = Math.sqrt(dxS * dxS + dyS * dyS);

        const ux = dxS / lenS;
        const uy = dyS / lenS;

        let nx = -uy;
        let ny =  ux;

        const midSX = (tx(seg.point_i.x) + tx(seg.point_j.x)) / 2;
        const midSY = (ty(seg.point_i.y) + ty(seg.point_j.y)) / 2;
        if (nx * (midSX - ox) + ny * (midSY - oy) < 0) { nx = -nx; ny = -ny; }

        const vi = seg.stress_i[this.selected_component];
        const vj = seg.stress_j[this.selected_component];

        const piPx = stressToPx(vi);
        const pjPx = stressToPx(vj);

        const biX = tx(seg.point_i.x) + nx * initialGapPx;
        const biY = ty(seg.point_i.y) + ny * initialGapPx;
        const bjX = tx(seg.point_j.x) + nx * initialGapPx;
        const bjY = ty(seg.point_j.y) + ny * initialGapPx;

        const tiX = biX + nx * piPx;
        const tiY = biY + ny * piPx;
        const tjX = bjX + nx * pjPx;
        const tjY = bjY + ny * pjPx;

        // Filled polygon
        g.append('polygon')
          .attr('points', `${biX},${biY} ${bjX},${bjY} ${tjX},${tjY} ${tiX},${tiY}`)
          .attr('fill', colors.stressFill)
          .attr('fill-opacity', 0.75)
          .attr('stroke', 'none');

        // Hatch overlay
        g.append('polygon')
          .attr('points', `${biX},${biY} ${bjX},${bjY} ${tjX},${tjY} ${tiX},${tiY}`)
          .attr('fill', 'url(#hatch-stress)')
          .attr('fill-opacity', 0.6)
          .attr('stroke', 'none');

        // Zero baseline
        g.append('line')
          .attr('x1', biX).attr('y1', biY)
          .attr('x2', bjX).attr('y2', bjY)
          .attr('stroke', colors.stressStroke)
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,2');

        // Tip line
        g.append('line')
          .attr('x1', tiX).attr('y1', tiY)
          .attr('x2', tjX).attr('y2', tjY)
          .attr('stroke', colors.stressStroke)
          .attr('stroke-width', 1.5);

        // End verticals
        g.append('line')
          .attr('x1', biX).attr('y1', biY)
          .attr('x2', tiX).attr('y2', tiY)
          .attr('stroke', colors.stressStroke).attr('stroke-width', 1);
        g.append('line')
          .attr('x1', bjX).attr('y1', bjY)
          .attr('x2', tjX).attr('y2', tjY)
          .attr('stroke', colors.stressStroke).attr('stroke-width', 1);

        // Stress value labels
        const labelOffset = 5;
        const anchorX   = nx >  0.3 ? 'start' : nx < -0.3 ? 'end' : 'middle';
        const baselineY = ny >  0.3 ? 'hanging' : ny < -0.3 ? 'auto' : 'middle';

        g.append('text')
          .attr('x', tiX + nx * labelOffset)
          .attr('y', tiY + ny * labelOffset)
          .attr('font-size', 9)
          .attr('fill', colors.text)
          .attr('text-anchor', anchorX)
          .attr('dominant-baseline', baselineY)
          .text(vi.toFixed(3));

        if (Math.abs(vj - vi) > 0.001) {
          g.append('text')
            .attr('x', tjX + nx * labelOffset)
            .attr('y', tjY + ny * labelOffset)
            .attr('font-size', 9)
            .attr('fill', colors.text)
            .attr('text-anchor', anchorX)
            .attr('dominant-baseline', baselineY)
            .text(vj.toFixed(3));
        }
      });

      // --- Column rectangle ---
      g.append('rect')
        .attr('x', tx(-halfC1))
        .attr('y', ty(halfC2))
        .attr('width', this.column_width * scale)
        .attr('height', this.column_depth * scale)
        .attr('fill', colors.column)
        .attr('fill-opacity', 0.5)
        .attr('stroke', colors.column)
        .attr('stroke-width', 1.5);

      // --- Shear perimeter segments ---
      this.shear_perimeter_polygon.forEach(seg => {
        g.append('line')
          .attr('x1', tx(seg.point_i.x))
          .attr('y1', ty(seg.point_i.y))
          .attr('x2', tx(seg.point_j.x))
          .attr('y2', ty(seg.point_j.y))
          .attr('stroke', seg.excluded ? colors.excluded : colors.perimeter)
          .attr('stroke-width', seg.excluded ? 1.5 : 2.5)
          .attr('stroke-dasharray', seg.excluded ? '4,3' : null);
      });

      // --- Column centroid axes ---
      const axisLen = 18;
      g.append('line')
        .attr('x1', tx(-axisLen / scale)).attr('y1', ty(0))
        .attr('x2', tx( axisLen / scale)).attr('y2', ty(0))
        .attr('stroke', colors.axis).attr('stroke-width', 1);
      g.append('text')
        .attr('x', tx(axisLen / scale) + 5).attr('y', ty(0) + 4)
        .attr('font-size', 10).attr('fill', colors.axis).text('x');

      g.append('line')
        .attr('x1', tx(0)).attr('y1', ty(-axisLen / scale))
        .attr('x2', tx(0)).attr('y2', ty( axisLen / scale))
        .attr('stroke', colors.axis).attr('stroke-width', 1);
      g.append('text')
        .attr('x', tx(0) + 5).attr('y', ty(axisLen / scale) - 4)
        .attr('font-size', 10).attr('fill', colors.axis).text('y');

      // --- Neutral axis ---
      g.append('line')
        .attr('x1', tx(-extentX - 18 / scale)).attr('y1', ty(this.cy))
        .attr('x2', tx( extentX + 18 / scale)).attr('y2', ty(this.cy))
        .attr('stroke', colors.neutral).attr('stroke-width', 1.2)
        .attr('stroke-dasharray', '6,3');

      g.append('line')
        .attr('x1', tx(this.cx)).attr('y1', ty(-extentY - 18 / scale))
        .attr('x2', tx(this.cx)).attr('y2', ty( extentY + 18 / scale))
        .attr('stroke', colors.neutral).attr('stroke-width', 1.2)
        .attr('stroke-dasharray', '6,3');

      g.append('text')
        .attr('x', tx(extentX + 20 / scale)).attr('y', ty(this.cy) + 4)
        .attr('font-size', 10).attr('fill', colors.neutral).text('N.A.');

      g.append('circle')
        .attr('cx', tx(this.cx)).attr('cy', ty(this.cy))
        .attr('r', 4).attr('fill', colors.neutral);
    },
  },
};
</script>

<style scoped>
.shear-diagram-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}
</style>